import { Injectable, SecurityContext } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
// Imported from the pinned npm package, never `window.katex`. The CDN tag this
// replaces was unpinned and rolled to a major version whose class names the
// bundled stylesheet did not match. Importing keeps JS and CSS on one version.
import katex from 'katex';
@Injectable({
    providedIn: 'root',
})
export class MarkdownService {
    private _agentHtmlSanitizeCache = new Map<string, SafeHtml>();
    private _katexCache = new Map<string, string>();
    private _contentPageHtmlCache = new Map<string, SafeHtml>();

    /** Placeholder a formula is parked under while the sanitizer runs. */
    private static readonly MATH_SLOT = '@@SAI_KATEX_SLOT_';
    private static readonly HTML_CACHE_MAX = 400;
    private static readonly KATEX_CACHE_MAX = 500;

    constructor(private sanitizer: DomSanitizer) {
        // Configure Marked options
        marked.setOptions({
            gfm: true,
            breaks: true,
        });
    }

    /**
     * Full agent-response pipeline: markdown → HTML (KaTeX) → scrollable tables → link targets.
     * Used by chat conversation and activities (keep in sync - no duplicate post-processing).
     */
    processAgentResponseHtml(markdown: string): string {
        const html = this.processMarkedText(markdown ?? '');
        return this.postProcessAgentHtml(html);
    }

    /**
     * SafeHtml for [innerHTML], sanitized first.
     *
     * `marked` v17 passes raw HTML in its input straight through (the `sanitize` option
     * was removed in v5), so handing its output to `bypassSecurityTrustHtml` disabled
     * Angular's sanitizer on attacker-controlled content. Any name that reaches a
     * markdown-rendered surface, such as an agent or report title, then
     * became stored XSS for every other user in the tenant.
     *
     * Angular's own sanitizer is used rather than a new dependency: it strips script
     * elements, event-handler attributes, and javascript: URLs while preserving the
     * formatting markdown legitimately produces. The result is re-trusted only after
     * that pass, so the value bound to [innerHTML] has already been cleaned.
     */
    sanitizeAgentHtml(html: string): SafeHtml {
        if (!html) return this.sanitizer.bypassSecurityTrustHtml('');
        let cached = this._agentHtmlSanitizeCache.get(html);
        if (!cached) {
            cached = this.sanitizer.bypassSecurityTrustHtml(
                this.sanitizeKeepingMath(html),
            );
            if (
                this._agentHtmlSanitizeCache.size >=
                MarkdownService.HTML_CACHE_MAX
            ) {
                // Keyed by the full HTML string, and the typing animation feeds a
                // new string every frame, so this map only ever grows. Drop it
                // wholesale rather than leak for the lifetime of the tab.
                this._agentHtmlSanitizeCache.clear();
            }
            this._agentHtmlSanitizeCache.set(html, cached);
        }
        return cached;
    }

    /**
     * Sanitize agent HTML without flattening rendered formulas.
     *
     * KaTeX expresses its entire layout in inline `style` attributes - the height
     * of each strut, and the `top` offset that lifts a numerator above the
     * fraction bar. `style` is not on Angular's allowed-attribute list, so a
     * straight `sanitize()` strips all of them and every stacked element
     * collapses onto one baseline; a fraction renders as its numerator and
     * denominator overprinted on each other.
     *
     * So each formula is lifted out before the sanitizer runs and re-rendered
     * afterwards. What gets carried across is only the LaTeX source, read back
     * from the `<annotation encoding="application/x-tex">` node KaTeX embeds in
     * its own output - never the markup. That keeps the XSS fix intact: HTML that
     * merely claims to be a formula is reduced to its annotation text and handed
     * to KaTeX, which escapes what it renders and (with `trust` left at its
     * default `false`) will not emit `\href`, `\url` or raw HTML. Nothing
     * attacker-authored is re-inserted after sanitizing.
     */
    private sanitizeKeepingMath(html: string): string {
        const sanitize = (value: string): string =>
            this.sanitizer.sanitize(SecurityContext.HTML, value) ?? '';

        // `.katex` is the wrapper KaTeX puts on every formula; skipping the DOM
        // work when it is absent keeps the common no-math path at one parse.
        if (typeof document === 'undefined' || !html.includes('katex')) {
            return sanitize(html);
        }

        const doc = this.parseInert(html);
        const holder = doc.body;
        if (!holder) return sanitize(html);

        const formulas: Array<{ latex: string; displayMode: boolean }> = [];
        holder.querySelectorAll('.katex').forEach((node) => {
            // A previous iteration may already have replaced an ancestor.
            if (!holder.contains(node)) return;

            const latex =
                node.querySelector(
                    'annotation[encoding="application/x-tex"]',
                )?.textContent ?? '';
            // Mid-stream during the typing animation the annotation has not
            // arrived yet. Leave the node for the sanitizer rather than drop it.
            if (!latex.trim()) return;

            // Display math is wrapped one level up; replacing the wrapper keeps
            // the block-level centring that `.katex-display` provides.
            const root = node.closest('.katex-display') ?? node;
            root.replaceWith(
                doc.createTextNode(
                    `${MarkdownService.MATH_SLOT}${formulas.length}@@`,
                ),
            );
            formulas.push({ latex, displayMode: root !== node });
        });

        let clean = sanitize(holder.innerHTML);
        formulas.forEach((formula, i) => {
            clean = clean
                .split(`${MarkdownService.MATH_SLOT}${i}@@`)
                .join(
                    this.renderKatex(formula.latex, {
                        displayMode: formula.displayMode,
                        throwOnError: false,
                    }),
                );
        });
        return clean;
    }

    /**
     * KaTeX render, memoised.
     *
     * Bound to the pinned npm package rather than a CDN global. The memo matters
     * because `sanitizeAgentHtml` runs on every change-detection pass and the
     * typing animation changes the HTML on every frame, so the same handful of
     * formulas would otherwise be re-parsed thousands of times per message.
     */
    private renderKatex(
        expr: string,
        options: { displayMode: boolean; throwOnError?: boolean },
    ): string {
        const key = `${options.displayMode}|${options.throwOnError}|${expr}`;
        const hit = this._katexCache.get(key);
        if (hit !== undefined) return hit;

        // Deliberately not wrapped in try/catch: callers that omit
        // `throwOnError` rely on the throw to render their own error markup.
        const rendered = katex.renderToString(expr, options);
        if (this._katexCache.size >= MarkdownService.KATEX_CACHE_MAX) {
            this._katexCache.clear();
        }
        this._katexCache.set(key, rendered);
        return rendered;
    }

    /** Markdown → SafeHtml (chat bubbles, activities answers, etc.). */
    renderAgentResponseSafe(
        markdown: string,
        options?: { stripBold?: boolean },
    ): SafeHtml {
        let html = this.processAgentResponseHtml(markdown);
        if (options?.stripBold) {
            html = html.replace(/<strong>([\s\S]*?)<\/strong>/gi, '$1');
        }
        return this.sanitizeAgentHtml(html);
    }

    private cleanInput(input: string): string {
        return (
            input
                .replace(/\u2014|&mdash;|&#8212;|&#x2014;/gi, '-')
                // 🔑 normalize inline math FIRST
                .replace(/\\\(([\s\S]+?)\\\)/g, '$$$1$$')

                // existing logic
                .replace(/\n\n/g, '\n\n<br>\n\n')
                .replace(/SafeValue must use \[property\]=binding:/g, '')
                .replace(/\(see https:\/\/g\.co\/ng\/security#xss\)/g, '')
        );
    }

    processWithoutMath(input: string): any {
        // Process Markdown input
        const processedInput = this.cleanInput(input);
        const rawHtml = marked.parse(processedInput);
        return this.sanitizer.bypassSecurityTrustHtml(this.sanitizer.sanitize(SecurityContext.HTML, rawHtml as string) ?? '');
    }

    /**
     * Markdown instruction editor preview (system instructions, agent custom instructions, ...).
     * Mirrors agent chat post-processing; uses GFM-safe prep so fences/tables parse.
     */
    processInstructionPreviewHtml(input: string): SafeHtml {
        const html = this.renderInstructionPreviewMarkdown(input ?? '');
        return this.sanitizeAgentHtml(this.postProcessAgentHtml(html));
    }

    processMarkedText(input: string): string {
        const processedInput = this.cleanInput(input);

        // 1️⃣ Render LaTeX blocks BEFORE markdown
        const mathStore: string[] = [];
        const text = processedInput.replace(/\\\[([\s\S]+?)\\\]/g, (_, expr) => {
            const key = `@@MATH_${mathStore.length}@@`;
            mathStore.push(
                this.renderKatex(expr.trim(), {
                    displayMode: true,
                    throwOnError: false,
                }),
            );
            return key;
        });

        // 2️⃣ Markdown parse
        let html = marked.parse(text) as string;

        // 3️⃣ Restore rendered math
        mathStore.forEach((rendered, i) => {
            html = html.replace(`@@MATH_${i}@@`, rendered);
        });

        // 4️⃣ Inline math - skip <code>/<pre> so column names like TEXT_ID$ are not paired with CF$_C_COLLECTOR
        html = this.applyInlineKatex(html);

        return html; // ⬅️ STRING, NOT SafeHtml
    }

    /** Stash fragile HTML fragments before $…$ KaTeX pass (prevents cross-section HTML corruption). */
    private vaultHtmlFragments(html: string): { html: string; vault: string[] } {
        const vault: string[] = [];
        const stash = (re: RegExp) => {
            html = html.replace(re, (match) => {
                const token = `@@HTML_VAULT_${vault.length}@@`;
                vault.push(match);
                return token;
            });
        };
        stash(/<pre[^>]*>[\s\S]*?<\/pre>/gi);
        stash(/<code[^>]*>[\s\S]*?<\/code>/gi);
        stash(/@@MATH_\d+@@/g);
        return { html, vault };
    }

    private restoreHtmlVault(html: string, vault: string[]): string {
        vault.forEach((fragment, i) => {
            html = html.split(`@@HTML_VAULT_${i}@@`).join(fragment);
        });
        return html;
    }

    /** True when $…$ content is likely LaTeX, not Oracle/SQL identifiers or HTML. */
    private looksLikeInlineMath(expr: string): boolean {
        const s = expr.trim();
        if (!s || s.length > 160) return false;
        if (/[<>]/.test(s)) return false;
        if (/^[`A-Z0-9_$.-]+$/i.test(s) && !/\\/.test(s)) return false;
        if (/\\[a-zA-Z]/.test(s)) return true;
        if (/[_^]/.test(s)) return true;
        if (/[+\-*/=]/.test(s) && /[a-zA-Z]/.test(s)) return true;
        return /^[A-Za-z]_\{?[0-9]+\}?$/.test(s);
    }

    private applyInlineKatex(html: string): string {
        const { html: safeHtml, vault } = this.vaultHtmlFragments(html);
        const rendered = safeHtml.replace(/\$([^$]+?)\$/g, (full, inner: string) => {
            if (!this.looksLikeInlineMath(inner)) return full;
            return this.renderKatex(inner.trim(), {
                displayMode: false,
                throwOnError: false,
            });
        });
        return this.restoreHtmlVault(rendered, vault);
    }

    private renderMathInHtml(html: string): string {
        const inlineMathRegex = /\$(.+?)\$/g;
        const blockMathRegex = /\[(.+?)\]/gs;

        // Helper function to decode HTML entities
        const decodeHtmlEntities = (text: string): string => {
            const textArea = document.createElement('textarea');
            textArea.innerHTML = text;
            return textArea.value.replace(/\u00A0/g, ' '); // Replace non-breaking space with normal space
        };

        const cleanMathExpr = (expr: string) => {
            return expr.replace(/^(<br>\s*)+|(\s*<br>)+$/g, '').trim();
        };

        // Decode HTML entities before processing
        html = decodeHtmlEntities(html);

        // Render block math expressions
        html = html.replace(blockMathRegex, (match, mathExpr) => {
            try {
                mathExpr = cleanMathExpr(mathExpr.replace(/%/g, '\\%'));
                return this.renderKatex(mathExpr, {
                    displayMode: true,
                });
            } catch (error) {
                console.error('Error rendering block math:', error);
                return '<span style="color: red;">Invalid KaTeX Expression</span>';
            }
        });

        // Render inline math expressions
        html = html.replace(inlineMathRegex, (match, mathExpr) => {
            try {
                mathExpr = cleanMathExpr(mathExpr.replace(/%/g, '\\%'));
                return this.renderKatex(mathExpr, {
                    displayMode: false,
                });
            } catch (error) {
                console.error('Error rendering inline math:', error);
                return '<span style="color: red;">Invalid KaTeX Expression</span>';
            }
        });

        return html;
    }

    /**
     * Markdown for platform content pages (Terms, Privacy, About), rendered the same way on every
     * surface that shows one, including the super admin preview.
     *
     * Deliberately NOT the agent pipeline: no KaTeX (a legal clause containing `$5` must not become
     * a formula), no `<br>` injection, and `breaks: false`, because authors write one list item or
     * paragraph per line and a soft wrap is not a line break. The backend already refuses raw HTML
     * and unsafe link schemes on write; this still runs Angular's sanitizer, because the renderer
     * must not depend on every writer being correct. Only absolute http(s) links open in a new tab;
     * `mailto:`, `tel:`, anchors and in-app paths keep their normal behaviour.
     */
    renderContentPageHtml(markdown: string): SafeHtml {
        const source = markdown ?? '';
        const cached = this._contentPageHtmlCache.get(source);
        if (cached) return cached;

        const raw = marked.parse(source, { gfm: true, breaks: false, async: false }) as string;
        let html = this.sanitizer.sanitize(SecurityContext.HTML, raw) ?? '';
        if (typeof document !== 'undefined' && html.includes('<a')) {
            const body = this.parseInert(html).body;
            body.querySelectorAll('a[href]').forEach((a) => {
                if (/^https?:\/\//i.test(a.getAttribute('href') ?? '')) {
                    a.setAttribute('target', '_blank');
                    a.setAttribute('rel', 'noopener noreferrer');
                }
            });
            html = body.innerHTML;
        }

        const safe = this.sanitizer.bypassSecurityTrustHtml(html);
        if (this._contentPageHtmlCache.size >= MarkdownService.HTML_CACHE_MAX) {
            this._contentPageHtmlCache.clear();
        }
        this._contentPageHtmlCache.set(source, safe);
        return safe;
    }

    // For handling raw HTML content
    processHtmlText(html: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(this.sanitizer.sanitize(SecurityContext.HTML, html) ?? '');
    }

    processSimpleBoldOnly(input: string): string {
        if (!input) return '';

        // Escape HTML first (prevent XSS since we're not sanitizing here)
        const escapeHtml = (text: string) =>
            text
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');

        let text = escapeHtml(input);

        // Convert **bold** → <strong>bold</strong>
        text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

        // Convert line breaks
        text = text.replace(/\n/g, '<br>');

        return text;
    }

    parseMarkdown(input: string): string {
        if (!input) return '';
        return marked.parse(input) as string;
    }

    /** Same marked + KaTeX path as processMarkedText, without cleanInput's <br> injection. */
    private renderInstructionPreviewMarkdown(input: string): string {
        const segments = input.split(/(```[\s\S]*?```)/g);
        const processedInput = segments
            .map((segment, index) => this.normalizeInstructionPreviewSegment(segment, index % 2 === 1))
            .join('');

        const mathStore: string[] = [];
        const text = processedInput.replace(/\\\[([\s\S]+?)\\\]/g, (_, expr) => {
            const key = `@@MATH_${mathStore.length}@@`;
            mathStore.push(
                this.renderKatex(expr.trim(), {
                    displayMode: true,
                    throwOnError: false,
                }),
            );
            return key;
        });

        let html = marked.parse(text) as string;

        mathStore.forEach((rendered, i) => {
            html = html.replace(`@@MATH_${i}@@`, rendered);
        });

        html = this.applyInlineKatex(html);

        return html;
    }

    private normalizeInstructionPreviewSegment(segment: string, isCodeFence: boolean): string {
        const text = segment
            .replace(/\u2014|&mdash;|&#8212;|&#x2014;/gi, '-')
            .replace(/\\\(([\s\S]+?)\\\)/g, '$$$1$$');

        if (isCodeFence) return text;

        return text
            .replace(/\n\n(?!\s*(?:\||```))/g, '\n\n<br>\n\n')
            .replace(/SafeValue must use \[property\]=binding:/g, '')
            .replace(/\(see https:\/\/g\.co\/ng\/security#xss\)/g, '');
    }

    /**
     * Parse agent HTML without giving it a chance to run.
     *
     * `document.createElement('div').innerHTML = html` looks inert but is not:
     * the nodes belong to the live document, so the browser starts fetching
     * `<img src=...>` immediately and an `onerror` handler fires - before the
     * sanitizer has seen the markup. A `DOMParser` document has no browsing
     * context, so no script runs and no subresource is requested. Every parse of
     * untrusted HTML in this service must go through here.
     */
    private parseInert(html: string): Document {
        return new DOMParser().parseFromString(html, 'text/html');
    }

    /** Table scroll wrappers + external links (shared by chat, activities, instruction preview). */
    private postProcessAgentHtml(html: string): string {
        if (!html || typeof document === 'undefined') return html;

        const doc = this.parseInert(html);
        const body = doc.body;
        if (!body) return html;

        body.querySelectorAll('table').forEach((table) => {
            if (
                table.parentElement?.classList.contains('marked-table-scroll')
            ) {
                return;
            }
            // Created from `doc`, so the node stays inside the inert document.
            const wrapper = doc.createElement('div');
            wrapper.className = 'marked-table-scroll';
            table.parentNode?.insertBefore(wrapper, table);
            wrapper.appendChild(table);
        });

        body.querySelectorAll('a').forEach((a) => {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
        });

        return body.innerHTML
            .replace(/SafeValue must use \[property\]=binding: ?/g, '')
            .replace(/\(see https:\/\/g\.co\/ng\/security#xss\)/g, '');
    }
}
